import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { PrintIcon, SyncIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'

import { API, constructApiUrl } from '../../../api/apis.config'
import { getCenter } from '../../../api/centers/get-center'
import { getCenterFinalCostVisibility } from '../../../api/centers/get-center-final-cost-visibility'
import { syncCenterCosts } from '../../../api/centers/sync-center-costs'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { CenterCosts } from '../../../components/pages/centers/center-costs'
import { centerCostsTableModeMapping } from '../../../components/pages/centers/center-costs/utils'
import { getCenterOlPermissions } from '../../../lib/constants/ol-permissions'
import { useObserver } from '../../../lib/contexts/observer'
import useAuth from '../../../lib/hooks/use-auth'
import useCurrency from '../../../lib/hooks/use-currency'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { CenterStatus, centerStatusMapping } from '../../../lib/types/enums/center/center-status'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { ProductTypeEnum } from '../../../lib/types/enums/product/product-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { Center } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { DataProps } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export function CostOfCenterPage(props: DataProps<Center>) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()
  const { modalState, toggleModal } = useModalManager<'synchronize'>(['synchronize'])
  const { id, data, isPending, refetch } = useDataProps(props, (id) => getCenter(id, 'FULL'))
  const { currency } = useCurrency()
  const { notify } = useObserver()
  const productTypes = data ? CenterProperties.getCenterProductTypes(data) : []

  const { mutateAsync: syncCenterCostsMutation, isPending: isSyncCenterCostsPending } =
    useMutation({
      mutationFn: syncCenterCosts,
      onError: showAxiosError
    })

  const { data: isFinalCost, isPending: isFinalCostPending } = useQuery({
    queryKey: ['final-cost-visibility', id],
    queryFn: () => getCenterFinalCostVisibility(id)
  })

  const mode = isFinalCost
    ? centerCostsTableModeMapping({
        costType: 'original-cost',
        role: user?.role,
        status: CenterStatus.VALIDATED
      })
    : centerCostsTableModeMapping({
        costType: 'original-cost',
        role: user?.role,
        status: data?.costsStatus
      })

  const validated = data?.costsStatus === CenterStatus.VALIDATED || isFinalCost
  const olPermissions = getCenterOlPermissions({
    type: data?.ol?.type,
    role: user.role,
    productTypes
  })

  if (isPending || !user || !data || isFinalCostPending) return <LoadingScreen />
  const { costsStatus } = data

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'h-2'} />
      <div className={'flex flex-row justify-between'}>
        {data?.ol?.type === OLTypeEnum.LEC ? (
          <div className={'flex flex-row gap-2 items-center'}>
            <Typography variant={'sub-heading'}>
              {t('center.costs.cost.of.center.and.money.transfers')}
            </Typography>

            <Badge
              text={t(centerStatusMapping[costsStatus].label)}
              variant={centerStatusMapping[costsStatus].variant}
            />
          </div>
        ) : (
          <div className={'flex flex-row gap-2 items-center'}>
            <Typography variant={'sub-heading'}>{t('center.costs.cost.of.center')}</Typography>
          </div>
        )}

        {user.role === RoleEnum.ADMIN && data?.ol?.type === OLTypeEnum.LEC && (
          <div className={'flex flex-row gap-2 items-center'}>
            {!validated && (
              <Button
                variant={'ghost'}
                icon={SyncIcon}
                label={t('center.costs.synchronize.costs')}
                disabled={isPending}
                onClick={() => toggleModal('synchronize')}
              />
            )}
            <Button
              variant={'ghost'}
              icon={PrintIcon}
              label={t('center.costs.print.costs')}
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.centers.printCenterCosts.url, { id })
                })
              }
            />
          </div>
        )}
      </div>

      {(olPermissions['center-costs-local-organizers'] ||
        olPermissions['center-costs-host-family']) && (
        <div
          className={cn('flex flex-row gap-4', {
            'flex-col': productTypes.includes(ProductTypeEnum.ONE_TO_ONE)
          })}
        >
          {olPermissions['center-costs-local-organizers'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.LocalOrganizers
                currency={currency}
                data={data}
                mode={mode['local-organizers']}
                costType={'original-cost'}
              />
            </InnerBox>
          )}

          {olPermissions['center-costs-host-family'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.HostFamily
                currency={currency}
                data={data}
                mode={mode['host-family']}
                costType={'original-cost'}
              />
            </InnerBox>
          )}
        </div>
      )}

      {(olPermissions['center-costs-tefl'] || olPermissions['center-costs-teaching-hall']) && (
        <div className={'flex flex-row gap-4'}>
          {olPermissions['center-costs-tefl'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.Tefl
                currency={currency}
                data={data}
                mode={mode.tefl}
                costType={'original-cost'}
              />
            </InnerBox>
          )}

          {olPermissions['center-costs-teaching-hall'] && (
            <InnerBox className={'w-full rounded-2xl'}>
              <CenterCosts.TeachingHall
                currency={currency}
                data={data}
                mode={mode['teaching-hall']}
                costType={'original-cost'}
              />
            </InnerBox>
          )}
        </div>
      )}

      {olPermissions['center-costs-cultural-outings'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.CulturalOutings
            currency={currency}
            data={data}
            mode={mode['cultural-outings']}
            costType={'original-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-arrival-departure-transfers'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.ArrivalDepartureTransfers
            currency={currency}
            data={data}
            mode={mode.transfers}
            costType={'original-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-french-leader'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.FrenchLeader
            id={id}
            currency={currency}
            costType={'original-cost'}
            mode={mode.budget}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-activities'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.Activities
            currency={currency}
            data={data}
            costType={'original-cost'}
            mode={mode.activities}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-miscellaneous'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <CenterCosts.Miscellaneous
            currency={currency}
            data={data}
            mode={mode.miscellaneous}
            costType={'original-cost'}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-total-costs'] && (
        <InnerBox className={'w-full rounded-2xl'}>
          <InnerBox className={'w-full rounded-xl bg-white-hover'}>
            <CenterCosts.TotalCosts data={data} costType={'original-cost'} currency={currency} />
          </InnerBox>
        </InnerBox>
      )}

      {olPermissions['center-costs-ol-payments'] && (
        <InnerBox className={'w-full rounded-2xl bg-white-hover'}>
          <CenterCosts.OlPayments
            data={data}
            centerId={id}
            currency={currency}
            olType={data.ol?.type}
          />
        </InnerBox>
      )}

      {olPermissions['center-costs-status'] && (
        <div className="flex flex-col gap-4">
          <CenterCosts.Status
            data={data}
            costType="original-cost"
            refetch={async () => {
              await refetch?.()
            }}
          />

          {data.costsStatus !== CenterStatus.OPEN &&
            data.costsSentToLecAt &&
            user.role === RoleEnum.Ol && (
              <Box
                className={
                  'flex flex-row items-center gap-2 shadow-[0px_0px_26px_0px_#93939333] rounded'
                }
              >
                <Typography variant={'secondary-body'} color={tailwindColors.navyBlue.DEFAULT}>
                  {t('center.costs.status.sent.to.lec.on')}
                </Typography>
                <Badge text={DateFormatter.DDMMYYYY(data.costsSentToLecAt)} variant={'primary'} />
              </Box>
            )}
        </div>
      )}

      <Modal
        isOpen={modalState.synchronize}
        variant="confirmation"
        onCancel={() => toggleModal('synchronize')}
        title={t('center.costs.synchronize.costs.validation.title')}
        icon={SyncIcon}
        isLoading={isSyncCenterCostsPending}
        actionButtonsOptions={{
          confirmText: t('center.costs.synchronize.costs.validation.confirm'),
          cancelText: t('center.costs.synchronize.costs.validation.cancel'),
          onConfirm: async () => {
            await syncCenterCostsMutation(id)
            // center.refetch()
            notify([
              'center-host-family-costs-table',
              'center-local-organizers-costs-table',
              'center-tefl-costs-table',
              'center-teaching-hall-costs-table',
              'center-total-costs'
            ])
            toggleModal('synchronize')
          }
        }}
      />
    </div>
  )
}
